<script setup>
import { computed } from "vue";
import Dialog from "primevue/dialog";
import ModalUpgrade from "@components/ModalUpgrade.vue";

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  clinic: {
    type: Object,
    required: true,
  },
  clinicNameField: {
    type: String,
    default: "business_name", // Default field name for clinic name
  },
});

const emit = defineEmits(["update:visible"]);

// Computed property to get the clinic name from the specified field
const clinicName = computed(() => {
  return props.clinic[props.clinicNameField] || "Clinic";
});

// Computed property for modal visibility with v-model support
const modalVisible = computed({
  get: () => props.visible,
  set: (value) => emit("update:visible", value),
});

// Function to toggle all patients to full course
const toggleFullCourseForAll = () => {
  // Set all adult vaccinations to full course
  props.clinic.basket.vaccinations.adult.forEach((patientVaccinations) => {
    patientVaccinations.forEach((vaccination) => {
      if (vaccination.dose > 1) {
        vaccination.course = "full";
      }
    });
  });

  // Set all child vaccinations to full course
  props.clinic.basket.vaccinations.child.forEach((patientVaccinations) => {
    patientVaccinations.forEach((vaccination) => {
      if (vaccination.dose > 1) {
        vaccination.course = "full";
      }
    });
  });
};
</script>

<template>
  <Dialog
    v-model:visible="modalVisible"
    modal
    :header="'Upgrade to full course - ' + clinicName"
    class="w-full max-w-[720px] max-h-[650px]">
    <div class="p-20 rounded-xl max-h-[500px] overflow-y-auto">
      <p class="mb-20">
        A full course is recommended for better and longer-lasting protection.
      </p>

      <div class="flex justify-end mb-10">
        <button class="text-blue underline" @click="toggleFullCourseForAll">
          Select full course for all
        </button>
      </div>

      <ul class="flex flex-col gap-4">
        <li
          v-for="(vaccinations, index) in clinic.basket.vaccinations.adult"
          :key="`upgrade-adult-${index}`">
          <ModalUpgrade
            type="adult"
            :patient="index"
            :vaccinations="vaccinations"
            :clinic="clinic"
            :opened="index === 0" />
        </li>
        <li
          v-for="(vaccinations, index) in clinic.basket.vaccinations.child"
          :key="`upgrade-child-${index}`">
          <ModalUpgrade
            type="child"
            :patient="index"
            :vaccinations="vaccinations"
            :clinic="clinic" />
        </li>
      </ul>
    </div>

    <template #footer>
      <button
        class="inline-block px-60 border border-blue text-blue text-center btn"
        @click="modalVisible = false">
        Close
      </button>
    </template>
  </Dialog>
</template>
